import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatNumber, getSortIndicator } from '../../../../utils/helpers'; // Keep formatNumber for salary
import useSortedData from '../../hooks/useSortedData';
import './EntrantsTeamsGolfRunning.css'; // Use Golf-specific CSS

const REFRESH_ICON = '\u21BB'; // Clockwise arrow
const PAUSE_ICON = '\u25A0'; // Black square
const PLAY_ICON = '\u25B6'; // Play symbol

// Placeholder component for displaying Golf entrants/teams in Running state
const EntrantsTeamsGolfRunning = ({ 
  contest, 
  currentUsername, 
  onTeamSelect, 
  onRefreshData,
  isAutoRefreshEnabled,
  onToggleAutoRefresh,
  refreshError 
}) => {
  // Add countdown state
  const [countdown, setCountdown] = useState(60);

  // Reset countdown when auto-refresh is toggled
  useEffect(() => {
    if (isAutoRefreshEnabled) {
      setCountdown(60);
    }
  }, [isAutoRefreshEnabled]);

  // Handle countdown and refresh
  useEffect(() => {
    let timer;
    if (isAutoRefreshEnabled) {
      if (countdown > 0) {
        timer = setInterval(() => {
          setCountdown(prev => prev - 1);
        }, 1000);
      } else {
        // When countdown hits 0, trigger refresh and reset countdown
        onRefreshData();
        setCountdown(60);
      }
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isAutoRefreshEnabled, countdown, onRefreshData]);

  // Create a map of player names to their finish, score, and status
  const playerDataMap = useMemo(() => {
    const map = {};
    contest.players.forEach(player => {
      map[player.name] = { 
        finish: player.finish, 
        // Use score from sportSpecificData, default to null if not present
        score: player.sportSpecificData?.score ?? null, 
        // ADD STATUS
        status: player.sportSpecificData?.status ?? null 
      };
    });
    return map;
  }, [contest.players]);

  // Calculate remaining players (golfers still playing - based on status)
  const calculateRemainingPlayers = useCallback((team) => {
    let remaining = 0;
    // Check captain
    const captainData = playerDataMap[team.captain];
    // Player is remaining if they exist and status is NOT 'cut'
    if (captainData && captainData.status !== 'cut') {
      remaining++;
    }
    // Check flex players
    team.flexPlayers.forEach(player => {
      const playerData = playerDataMap[player];
      // Player is remaining if they exist and status is NOT 'cut'
      if (playerData && playerData.status !== 'cut') {
        remaining++;
      }
    });
    return remaining;
  }, [playerDataMap]);

  // Calculate current team score (sum scores of ALL players with a score)
  const calculateCurrentScore = useCallback((team) => {
    let totalScore = 0;
    let playersScoringCount = 0; 

    // Process Captain
    const captainStatus = playerDataMap[team.captain];
    if (captainStatus && typeof captainStatus.score === 'number') { 
      totalScore += captainStatus.score;
      playersScoringCount++;
    }

    // Process Flex Players
    team.flexPlayers.forEach(player => {
      const playerStatus = playerDataMap[player];
      if (playerStatus && typeof playerStatus.score === 'number') { 
        totalScore += playerStatus.score;
        playersScoringCount++;
      }
    });
    
    return playersScoringCount > 0 ? totalScore : 0; 
  }, [playerDataMap]);

  // Check if ALL players on a team have status 'cut'
  const calculateIsEliminated = useCallback((team) => {
    const captainData = playerDataMap[team.captain];
    // If captain doesn't exist or status is not 'cut', team is not eliminated
    if (!captainData || captainData.status !== 'cut') {
      return false;
    }

    // Check all flex players. If *any* player doesn't exist or status is not 'cut', team is not eliminated
    for (const playerName of team.flexPlayers) {
      const playerData = playerDataMap[playerName];
      if (!playerData || playerData.status !== 'cut') {
        return false;
      }
    }

    // If we reach here, captain and all flex players are 'cut'
    return true;
  }, [playerDataMap]);

  // Pre-process entrants to include place, remaining, currentScore, and isEliminated
  const processedEntrants = useMemo(() => {
    // Calculate captain's score for each entrant
    const entrantsWithCaptainScores = contest.entrants.map(entrant => {
      const captainData = playerDataMap[entrant.team.captain];
      return {
        ...entrant,
        currentScore: calculateCurrentScore(entrant.team),
        captainScore: captainData?.score ?? null
      };
    });

    // Sort by current score (ASCENDING, nulls/not scored last), then by captain score (ascending) as tiebreaker
    const sortedByScore = [...entrantsWithCaptainScores].sort((a, b) => {
      const scoreA = a.currentScore;
      const scoreB = b.currentScore;

        if (scoreA === null && scoreB === null) return 0;
        if (scoreA === null) return 1; // Push nulls (not scored) to the end
        if (scoreB === null) return -1;
        
        const scoreDiff = scoreA - scoreB; 
        if (scoreDiff !== 0) return scoreDiff;

      // If scores are equal, use captain's score as tiebreaker
      const captainScoreA = a.captainScore;
      const captainScoreB = b.captainScore;

      if (captainScoreA === null && captainScoreB === null) return 0;
      if (captainScoreA === null) return 1;
      if (captainScoreB === null) return -1;

      return captainScoreA - captainScoreB;
    });

    // Process ranks with ties
    let currentRank = 1;
    let currentScore = null;
    let currentCaptainScore = null;
    let tieCount = 0;

    return sortedByScore.map((entrant, index) => {
      // Check if this entrant has the same score as the previous one
      const isTiedWithPrevious = currentScore === entrant.currentScore && 
                                currentCaptainScore === entrant.captainScore;
      
      if (isTiedWithPrevious) {
        tieCount++;
      } else {
        // New score, update rank and reset tie count
        currentRank = index + 1;
        tieCount = 0;
        currentScore = entrant.currentScore;
        currentCaptainScore = entrant.captainScore;
      }

      // Look ahead to see if there are more ties
      let lookAheadIndex = index + 1;
      while (lookAheadIndex < sortedByScore.length) {
        const nextEntrant = sortedByScore[lookAheadIndex];
        if (nextEntrant.currentScore === entrant.currentScore && 
            nextEntrant.captainScore === entrant.captainScore) {
          tieCount++;
          lookAheadIndex++;
        } else {
          break;
        }
      }

      return {
      ...entrant,
        place: tieCount > 0 ? `T${currentRank}` : currentRank,
        remaining: calculateRemainingPlayers(entrant.team),
      displayName: entrant.entryName || entrant.username,
      teamSalary: entrant.teamSalary,
      isEliminated: calculateIsEliminated(entrant.team)
      };
    });
  }, [
    contest.entrants,
    playerDataMap,
    calculateRemainingPlayers,
    calculateCurrentScore,
    calculateIsEliminated
  ]);

  // Apply sorting hook, initializing with key and direction arguments
  const { sortedData, requestSort, sortConfig } = useSortedData(
    processedEntrants,
    'currentScore', // Initial Key (2nd argument)
    'ascending'     // Initial Direction (3rd argument)
  );

  // Format score for display
  const formatScore = (score) => {
    if (score === null) return '-';
    if (score === 0) return 'E';
    return score > 0 ? `+${score}` : score.toString();
  };

  return (
    <div className="entrants-teams-container-golf">
      <div className="entrants-teams-header">
        <span className="header-title-group">
          <span>Standings</span>
          <span className="header-info-text">(Click team to view)</span>
          {refreshError && (
            <span className="header-error-text">Refresh failed: {refreshError}</span>
          )}
        </span>
        <div className="header-buttons">
          <span className="auto-refresh-text">
            auto-refresh: <span className="countdown-text">{isAutoRefreshEnabled ? `${countdown}s` : 'off'}</span>
          </span>
          <button 
            onClick={onToggleAutoRefresh} 
            className="refresh-button"
            title={isAutoRefreshEnabled ? "Pause auto-refresh" : "Resume auto-refresh"}
          >
            {isAutoRefreshEnabled ? 'on' : 'off'}
          </button>
          <button 
            onClick={onRefreshData} 
            className="refresh-button" 
            title="Manual Refresh"
          >
          {REFRESH_ICON}
        </button>
        </div>
      </div>
      <div className="entrants-teams-table-container">
        <table className="entrants-teams-table">
          <thead>
            <tr>
              {/* Order: Rank | User | Salary | Rem (Desktop) | Score */}
              <th onClick={() => requestSort('place')}>Rank {getSortIndicator(sortConfig, 'place')}</th>
              <th onClick={() => requestSort('displayName')}>User {getSortIndicator(sortConfig, 'displayName')}</th>
              {/* Show Salary on Mobile */}
              <th onClick={() => requestSort('teamSalary')}>Salary {getSortIndicator(sortConfig, 'teamSalary')}</th>
              {/* Show Rem on Desktop only - moved before Score */}
              <th className="show-on-desktop" onClick={() => requestSort('remaining')}>Remaining {getSortIndicator(sortConfig, 'remaining')}</th>
              {/* Sort score ascending */}
              <th onClick={() => requestSort('currentScore', true)}>Score {getSortIndicator(sortConfig, 'currentScore')}</th> 
            </tr>
          </thead>
          <tbody>
            {sortedData.map((entrant) => {
              const isCurrentUser = entrant.username?.toLowerCase() === currentUsername?.toLowerCase();
              // CHANGE LOGIC FOR teamStatusClass
              const teamStatusClass = entrant.isEliminated ? 'team-eliminated' : 'team-active';
              const userClass = isCurrentUser ? 'current-user-team' : '';

              return (
                <tr
                  key={entrant._id}
                  onClick={() => onTeamSelect(entrant)}
                  className={`${userClass} ${teamStatusClass}`.trim()}
                >
                  {/* Order: Rank | User | Salary | Rem (Desktop) | Score */}
                  <td>{entrant.place}</td>
                  <td>
                    {entrant.displayName}
                  </td>
                   {/* Show Salary on Mobile */}
                  <td>${formatNumber(entrant.teamSalary)}</td>
                  {/* Show Rem on Desktop only - moved before Score */}
                  <td className="show-on-desktop">{entrant.remaining}</td>
                  {/* Display formatted score */}
                  <td>{formatScore(entrant.currentScore)}</td> 
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

EntrantsTeamsGolfRunning.propTypes = {
  contest: PropTypes.shape({
    entrants: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
      entryName: PropTypes.string,
      // Cashes not directly used, score calculated
      team: PropTypes.shape({
        captain: PropTypes.string.isRequired,
        flexPlayers: PropTypes.arrayOf(PropTypes.string).isRequired
      }).isRequired,
      teamSalary: PropTypes.number
    })).isRequired,
    players: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      finish: PropTypes.number, // 0/null = active, >0 = finished rank, -1 = CUT/WD?
      salary: PropTypes.number,
      sportSpecificData: PropTypes.shape({
        score: PropTypes.number // Player's score relative to par or total
      })
    })).isRequired,
    // Payouts might still be relevant but not used directly in this table
    playerPayouts: PropTypes.any, 
    entrantPayouts: PropTypes.any,
  }).isRequired,
  currentUsername: PropTypes.string, // Can be null/undefined if user not logged in
  onTeamSelect: PropTypes.func.isRequired,
  onRefreshData: PropTypes.func.isRequired, // Added prop type
  isAutoRefreshEnabled: PropTypes.bool.isRequired,
  onToggleAutoRefresh: PropTypes.func.isRequired,
  refreshError: PropTypes.string,
};

export default EntrantsTeamsGolfRunning; 